import type { AxiosError } from "axios";
import axios from "axios";

import { env } from "../env";

// Create an axios instance for making API requests
export const axiosInstance = axios.create({
  // Use a function to dynamically get the baseURL on each request
  baseURL: env.FLUID_API_BASE_URL || "https://api.fluid.app",
  timeout: env.FLUID_API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// Create type declaration for the window object with our custom property
declare global {
  interface Window {
    __FLUID_API_BASE_URL?: string;
  }
}

// Add an interceptor to dynamically update the baseURL on each request
// This allows the baseURL to be changed after the axios instance is created
axiosInstance.interceptors.request.use((config) => {
  // Priority order depends on environment:
  // In browser: 1. Browser global (from data attribute) 2. Environment variable 3. Default URL
  // In Node.js: 1. Environment variable 2. Default URL
  let apiBaseUrl = "https://api.fluid.app"; // Default

  // Environment variable takes priority in Node.js environments or when browser global is not set
  if (env.FLUID_API_BASE_URL) {
    apiBaseUrl = env.FLUID_API_BASE_URL;
  }

  // Browser global takes highest priority when available (overrides environment variable)
  if (typeof window !== "undefined" && window.__FLUID_API_BASE_URL) {
    apiBaseUrl = window.__FLUID_API_BASE_URL;
  }

  config.baseURL = apiBaseUrl;
  return config;
});

// Add a response interceptor to track successful requests
axiosInstance.interceptors.response.use(
  (response) => {
    console.log("✅ Request successful to:", response.config.url);
    return response;
  },
  (error: AxiosError) => {
    console.error("❌ Request failed:", error.config?.url);
    return Promise.reject(error);
  },
);

/**
 * Handle API errors with custom messages
 * @param error - The error object
 * @param message - Custom error message
 */
export const handleApiError = (error: Error, message: string): void => {
  if (env.FLUID_API_DEBUG) {
    console.error(`${message}: ${error.message}`);

    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        console.error("Response data:", axiosError.response.data);
        console.error("Response status:", axiosError.response.status);
      } else if (axiosError.request) {
        console.error("Request failed with no response");
      }
    }
  }
};
